import { apiClient, toQuery, saveBlob } from "./client";

// No params → bare array (legacy consumers). With { limit } → the
// { items, total, limit, offset } envelope. Other params: q, status (array),
// light (picker payload), show_all, sort, dir, offset.
export async function fetchMyJobs(params = {}) {
  const query = toQuery(params);
  const res = await apiClient("get", `/jobs${query ? `?${query}` : ""}`);
  return res.data;
}

/** Geocoded jobs for /map: `{ points: [...], unlocated }`. `today` (yyyy-MM-dd,
 *  the viewer's date) decides each point's `visit_today`. */
export async function fetchJobMap(today) {
  const res = await apiClient("get", `/jobs/map?today=${today}`);
  return res.data;
}

export async function createJob(payload) {
  const res = await apiClient("post", "/jobs", payload);
  return res.data;
}

export async function fetchJobDetail(jobId) {
  const res = await apiClient("get", `/jobs/${jobId}`);
  return res.data;
}

export async function addJobNote(jobId, content) {
  const res = await apiClient("post", `/jobs/${jobId}/notes`, {
    content,
  });
  return res.data;
}

/** Edit an existing job note's text — management only, any note. */
export async function updateJobNote(jobId, noteId, content) {
  const res = await apiClient("patch", `/jobs/${jobId}/notes/${noteId}`, { content });
  return res.data;
}

export async function uploadJobPhoto(jobId, file) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await apiClient("post", `/jobs/${jobId}/photos`, formData);
  return res.data;
}

export async function updateJob(jobId, payload) {
  const res = await apiClient("patch", `/jobs/${jobId}`, payload);
  return res.data;
}

export async function assignTechnician(jobId, technicianId) {
  const res = await apiClient("post", `/jobs/${jobId}/assign`, {
    technician_id: technicianId,
  });
  return res.data;
}

export async function deleteJob(jobId, deleteChildren = false) {
  const res = await apiClient("delete", `/jobs/${jobId}?delete_children=${deleteChildren}`);
  return res.data;
}

export async function fetchJobEditLog(jobId) {
  const res = await apiClient("get", `/jobs/${jobId}/edit-log`);
  return res.data;
}

export async function uploadJobAttachment(jobId, attachmentType, file) {
  const formData = new FormData();
  formData.append("attachment_type", attachmentType);
  formData.append("file", file);
  const res = await apiClient("post", `/jobs/${jobId}/attachments`, formData);
  return res.data;
}

export async function deleteJobAttachment(jobId, attachmentId) {
  const res = await apiClient("delete", `/jobs/${jobId}/attachments/${attachmentId}`);
  return res.data;
}

export async function deleteJobPhoto(jobId, photoId) {
  const res = await apiClient("delete", `/jobs/${jobId}/photos/${photoId}`);
  return res.data;
}

// Downloads the job's PDF report and triggers a browser save dialog.
export async function downloadJobReport(jobId, jobNumber, visitIds) {
  // visitIds omitted/null → full report; an array → only those visits.
  // An EMPTY array means "no visits at all" — HTTP can't express an empty
  // repeated param, so send an impossible id (ids start at 1) to match nothing.
  const qs = Array.isArray(visitIds)
    ? "?" + (visitIds.length ? visitIds : [0]).map((id) => `visit_ids=${id}`).join("&")
    : "";
  const res = await apiClient("get", `/jobs/${jobId}/report${qs}`, undefined, {
    responseType: "blob",
  });
  savePdfBlob(res.data, reportFilename(res, jobNumber || jobId));
}

// Generates a CUSTOMIZED report. A POST because note_overrides carries
// rewritten prose, which does not belong in a querystring.
// `options` — all optional, omitted means "include everything":
//   visit_ids, include_sections, note_overrides, exclude_note_ids,
//   exclude_photo_ids, job_note_overrides, exclude_job_note_ids, save
// ⚠️ note_overrides changes only the PRINTED text; the stored note is untouched.
// `options.save: true` also persists the generation as a JobReport row (the
// job's Reports tab) — the backend always returns the PDF bytes regardless;
// `download` (default true) decides whether THIS call also triggers a
// browser save dialog, so "Save to Job" alone can skip it.
export async function generateJobReport(jobId, jobNumber, options = {}, download = true) {
  const res = await apiClient("post", `/jobs/${jobId}/report`, options, {
    responseType: "blob",
  });
  if (download) {
    savePdfBlob(res.data, reportFilename(res, jobNumber || jobId));
  }
  return res.data;
}

// The server names the file (customer-description-job#-date); the fallback
// only applies if the header is missing (e.g. an older backend).
function reportFilename(res, fallbackId) {
  const m = /filename="([^"]+)"/.exec(res.headers?.["content-disposition"] || "");
  return m ? m[1] : `job-report-${fallbackId}.pdf`;
}

function savePdfBlob(data, filename) {
  saveBlob(data, filename, "application/pdf");
}

// The reports saved to this job via "Save to Job" / "Download and Save to Job".
export async function fetchJobReports(jobId) {
  const res = await apiClient("get", `/jobs/${jobId}/reports`);
  return res.data;
}

// Downloads a zip of the selected job-level photos (the ones on the job's own
// Photos section — visit photos have their own endpoint, in api/visits.js).
// With `groupByVisit` the archive is folders: "Job Photos" + one per visit.
export async function downloadJobPhotosZip(jobId, photoIds, { groupByVisit = false } = {}) {
  const qs = photoIds.map((id) => `photo_ids=${id}`).join("&") + (groupByVisit ? "&group_by_visit=true" : "");
  const res = await apiClient("get", `/jobs/${jobId}/photos/download?${qs}`, undefined, {
    responseType: "blob",
  });
  saveBlob(res.data, `job-${jobId}-photos.zip`, "application/zip");
}
